/**
 * HITL Action proposal — wire shape distilled from v1
 * ``app/types/chat.ts::ActionProposal``.
 *
 * The full v1 type carries 12+ optional preview sub-shapes (one per action
 * type — send_email, create_event, salesforce_action, …). Those live in
 * ``apps/client/features/chat/types/actions.ts`` (vertical-specific). Here
 * we expose only the envelope every HITL surface needs:
 *   - identification (``id`` / ``action_type``),
 *   - moderator UX (``status`` / ``risk_level`` / ``title`` / ``description``),
 *   - audit trail (``proposed_fields`` / ``executed_fields`` / timestamps),
 *   - capabilities (``reversible`` / ``undo_available``).
 */

import { z } from "zod";
import { IsoTimestampSchema, UuidSchema } from "./common.js";

export const ActionStatusSchema = z.enum([
  "pending",
  "approved",
  "rejected",
  "executing",
  "executed",
  "failed",
  "expired",
  "clarification_needed",
]);
export type ActionStatus = z.infer<typeof ActionStatusSchema>;

export const RiskLevelSchema = z.enum(["low", "medium", "high"]);
export type RiskLevel = z.infer<typeof RiskLevelSchema>;

export const ActionSchema = z.object({
  // The v1 wire field is ``proposal_id`` — we alias as ``id`` via output
  // mapping in the call-site, but keep the BE field name on the parser.
  id: UuidSchema,
  action_type: z.string(),
  user_id: UuidSchema,
  conversation_id: UuidSchema.optional(),
  query_id: z.string().optional(),
  status: ActionStatusSchema,
  risk_level: RiskLevelSchema,
  title: z.string(),
  description: z.string(),
  icon: z.string().optional(),
  proposed_fields: z.record(z.string(), z.unknown()).optional(),
  executed_fields: z.record(z.string(), z.unknown()).optional(),
  reversible: z.boolean(),
  undo_available: z.boolean(),
  requires_confirmation: z.boolean(),
  created_at: IsoTimestampSchema,
  expires_at: IsoTimestampSchema,
  executed_at: IsoTimestampSchema.optional(),
  result_message: z.string().optional(),
  error_message: z.string().optional(),
  clarification_message: z.string().optional(),
  clarification_missing: z.array(z.string()).optional(),
});
export type Action = z.infer<typeof ActionSchema>;
